/* eslint-disable max-statements */
import React from 'react';
import styles from './styles.module.scss';
import icCopy from 'assets/svg/ic_copy_V2.svg';
import { useTypedDispatch, useTypedSelector } from '@profile/src/redux/utils';
import { fetchIngameBalance, updateWalletBalance } from '@profile/src/redux/actions';
import CoinCard from './Components/CoinCard';
import TotalBalance from './Components/TotalBalance';
import classNames from 'classnames';
import IngameCoinCard from './Components/IngameCoinCard';
import WalletProvider, { useWalletContext } from './WalletProvider';
import { ellipsisMiddle } from '@profile/src/utils/text';
import { InGameCurrency, MainCurrency } from './config';
import { useUserWalletContext } from '../../UserWalletProvider';
import { useTooltip } from '@profile/src/Components/TooltipProvider';
import { TooltipDirection } from '@profile/src/Components/TooltipProvider/Tooltip';
import BreakpointContent from '@profile/src/Components/BreakpointContent';
import { getFeatureToggle } from '@wolffun/analytics';
import { AppError } from '@profile/src/redux/types';
import icIngame from 'assets/svg/ic_game_pad_V2.svg';
import icWallet from 'assets/svg/ic_wallet_V2.svg';
import icInfo from 'assets/svg/ic_info_outlined_V2.svg';
import Tooltip from '@profile/src/Components/Tooltip';
import { TooltipPosition } from '@profile/src/Components/Tooltip/types';
import WalletConnectConfirmProvider from '@profile/src/Components/WalletConnectConfirmProvider';
import { copyToClipboard } from '@profile/src/utils/copyToClipboard';
const INTERVAL_COUNTDOWN = 30; //seconds

const WalletContainer = () => {
  const dispatch = useTypedDispatch();
  const {
    walletError,
    isWrongAddress,
    isUserBanned
  } = useUserWalletContext();
  const {
    startExchange,
    startClaimToken,
    showMaintenance
  } = useWalletContext();
  const walletAddress = useTypedSelector(state => {
    var _state$userProfile$us;

    return ((_state$userProfile$us = state.userProfile.user) === null || _state$userProfile$us === void 0 ? void 0 : _state$userProfile$us.address) || '';
  });
  const thcBalance = useTypedSelector(state => state.connectWallet.thcBalance);
  const nativeBalance = useTypedSelector(state => state.connectWallet.wbnbBalance);
  const busdtBalance = useTypedSelector(state => state.connectWallet.busdtBalance);
  const claimError = useTypedSelector(state => state.claimToken.error);
  const confirmCashoutError = useTypedSelector(state => state.confirmCashout.error);
  const thgBalance = useTypedSelector(state => state.connectWallet.thgBalance);
  const ingameBalance = useTypedSelector(state => state.ingameBalance.data);
  const imgRef = React.useRef(null);
  const spanRef = React.useRef(null);
  const tooltip = useTooltip();
  const {
    modal: walletModal
  } = useWalletContext();
  const [counter, setCounter] = React.useState(INTERVAL_COUNTDOWN);
  const [isDisabledWalletDeposit, setIsDisabledWalletDeposit] = React.useState(false);
  const isDisableButton = isDisabledWalletDeposit || !!walletError || isWrongAddress || isUserBanned;
  React.useEffect(() => {
    const fetchToggleFeature = async () => {
      const isDisabled = await getFeatureToggle('market_disable_wallet_deposit');
      setIsDisabledWalletDeposit(isDisabled === 'true');
    };

    fetchToggleFeature();
  }, []);
  React.useEffect(() => {
    dispatch(updateWalletBalance());
    dispatch(fetchIngameBalance());
  }, []);
  React.useEffect(() => {
    if ([claimError, confirmCashoutError].includes(AppError.MAINTENANCE_CASHOUT)) {
      showMaintenance();
    }
  }, [claimError, confirmCashoutError, showMaintenance]);
  React.useEffect(() => {
    const timeout = setTimeout(() => {
      if (counter === 0) {
        if (!(walletModal !== null && walletModal !== void 0 && walletModal.claimToken)) {
          // the claim modal is open, so we don't count down the counter
          dispatch(updateWalletBalance());
          dispatch(fetchIngameBalance());
        }

        setCounter(INTERVAL_COUNTDOWN);
      } else {
        setCounter(counter - 1);
      }
    }, 1000);
    return () => clearTimeout(timeout);
  }, [counter, walletModal]);

  const onPress = (top, left) => {
    if (imgRef.current) {
      copyToClipboard(walletAddress);
      tooltip.show('Copied', {
        top,
        left
      }, TooltipDirection.BOTTOM, {
        backgroundColor: '#261858',
        textColor: '#fff',
        delay: 1500
      });
    }
  };

  const renderWallet = () => {
    return /*#__PURE__*/React.createElement("div", {
      className: styles.wallet
    }, /*#__PURE__*/React.createElement(TotalBalance, {
      className: styles.coinCard,
      isWrongNetwork: !!walletError,
      nativeBalance: nativeBalance,
      fiatBalance: busdtBalance
    }), /*#__PURE__*/React.createElement(CoinCard, {
      className: styles.coinCard,
      isWrongNetwork: !!walletError,
      amount: thcBalance,
      onClickExchange: startExchange,
      coinType: MainCurrency.THETAN_COIN,
      disabled: isDisableButton
    }), /*#__PURE__*/React.createElement(CoinCard, {
      className: styles.coinCard,
      isWrongNetwork: !!walletError,
      amount: thgBalance,
      onClickExchange: startExchange,
      disabled: isDisableButton,
      coinType: MainCurrency.THETAN_GEM
    }));
  };

  const renderIngameCurrency = () => {
    return /*#__PURE__*/React.createElement("div", {
      className: classNames(styles.wallet, styles.walletIngame, styles.show)
    }, /*#__PURE__*/React.createElement(IngameCoinCard, {
      className: styles.coinCard,
      claimable: true,
      amount: ingameBalance.balance.gTHC,
      onClickClaim: startClaimToken,
      coinType: InGameCurrency.INGAME_THETAN_COIN,
      disabled: isDisableButton
    }), /*#__PURE__*/React.createElement(IngameCoinCard, {
      className: styles.coinCard,
      claimable: true,
      amount: ingameBalance.balance.gTHG,
      onClickClaim: startClaimToken,
      coinType: InGameCurrency.INGAME_THETAN_GEM,
      disabled: true
    }), /*#__PURE__*/React.createElement(IngameCoinCard, {
      className: styles.coinCard,
      claimable: false,
      amount: ingameBalance.balance.gPP,
      onClickClaim: startClaimToken,
      coinType: InGameCurrency.INGAME_POWER_POINT,
      disabled: isDisableButton
    }));
  };

  const renderAddress = () => {
    return /*#__PURE__*/React.createElement("div", {
      className: styles.code
    }, /*#__PURE__*/React.createElement("span", {
      ref: spanRef,
      onClick: () => {
        if (spanRef.current) {
          const rect = spanRef.current.getBoundingClientRect();
          onPress(rect.top + document.documentElement.scrollTop + 35, rect.left + document.documentElement.scrollLeft + rect.width / 2);
        }
      },
      className: styles.uuid
    }, ellipsisMiddle(walletAddress)), /*#__PURE__*/React.createElement("img", {
      ref: imgRef,
      className: styles.icCopy,
      onClick: () => {
        if (imgRef.current) {
          const rect = imgRef.current.getBoundingClientRect();
          onPress(rect.top + document.documentElement.scrollTop + 40, rect.left + document.documentElement.scrollLeft + rect.width / 2);
        }
      },
      src: icCopy
    }));
  };

  const renderIngameLabel = () => {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: classNames(styles.header, styles.ingame)
    }, /*#__PURE__*/React.createElement("img", {
      src: icIngame,
      className: styles.icIngame
    }), /*#__PURE__*/React.createElement("span", {
      className: styles.label
    }, "Earn Wallet"), /*#__PURE__*/React.createElement(Tooltip, {
      className: styles.tooltip,
      color: "#23176B",
      content: /*#__PURE__*/React.createElement("div", {
        className: styles.content
      }, "Balance on your in-game wallet"),
      position: TooltipPosition.BOTTOM
    }, /*#__PURE__*/React.createElement("img", {
      src: icInfo,
      className: styles.icInfo
    }))), /*#__PURE__*/React.createElement("div", {
      className: classNames(styles.horizonalBar)
    }));
  };

  return /*#__PURE__*/React.createElement("div", {
    className: styles.container
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames(styles.header, styles.headerCoin)
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.label
  }, /*#__PURE__*/React.createElement("img", {
    src: icWallet,
    className: styles.icWallet
  }), /*#__PURE__*/React.createElement("span", null, "Web3 Wallet"), /*#__PURE__*/React.createElement(Tooltip, {
    className: styles.tooltip,
    color: "#23176B",
    content: /*#__PURE__*/React.createElement("div", {
      className: styles.content
    }, "Balance on your web3 wallet"),
    position: TooltipPosition.BOTTOM
  }, /*#__PURE__*/React.createElement("img", {
    src: icInfo,
    className: styles.icInfo
  }))), /*#__PURE__*/React.createElement(BreakpointContent, {
    renderPhone: () => null,
    renderDesktop: renderAddress,
    renderTablet: renderAddress
  })), /*#__PURE__*/React.createElement("div", {
    className: styles.horizonalBar
  }), /*#__PURE__*/React.createElement(BreakpointContent, {
    renderPhone: renderAddress,
    renderDesktop: () => null,
    renderTablet: () => null
  }), renderWallet(), renderIngameLabel(), renderIngameCurrency());
};

const WalletPage = () => {
  return /*#__PURE__*/React.createElement(WalletConnectConfirmProvider, null, /*#__PURE__*/React.createElement(WalletProvider, null, /*#__PURE__*/React.createElement(WalletContainer, null)));
};

export default WalletPage;